import React, {useMemo} from 'react';

const colors = ['#69D994', '#9DDEAD', '#B3E5C2', '#E5F6EB'];
const orbitCount = 5;
const circlesPerOrbit = 3;
const complexOrbits = 5;

const randRange = (min: number, max: number) => (
	Math.random() * (max - min) + min
);

const useCircularOrbits = () => (
	useMemo(() => {
		const generated: JSX.Element[] = [];

		// Simple orbits
		for (let orbitIndex = 0; orbitIndex < orbitCount; orbitIndex++) {
			const centerX = 50;
			const centerY = 50;
			const orbitRadius = 8 + orbitIndex * 8;
			const colorIndex = orbitIndex % 4;
			const direction = orbitIndex % 2 === 0 ? 1 : -1;

			for (let i = 0; i < circlesPerOrbit; i++) {
				const offset = (360 / circlesPerOrbit) * i;
				const circleSize = 3 - (orbitIndex * 0.4);
				const duration = 10 + orbitIndex * 3;

				generated.push(
					<circle
						key={`orbit-${orbitIndex}-${i}`}
						cx={centerX + orbitRadius}
						cy={centerY}
						r={circleSize}
						fill={colors[colorIndex]}
						opacity='0.8'
					>
						<animateTransform
							attributeName='transform'
							type='rotate'
							from={`${offset} ${centerX} ${centerY}`}
							to={`${offset + (360 * direction)} ${centerX} ${centerY}`}
							dur={`${duration}s`}
							repeatCount='indefinite'
							calcMode='linear'
						/>
					</circle>
				);

				// Add secondary orbits for some circles
				if (i % 2 === 0 && orbitIndex < 3) {
					const secondaryRadius = 3;
					const secondarySize = circleSize * 0.6;
					const secondaryColor = colors[(colorIndex + 1) % 4];
					const secondaryDuration = duration * 0.3;

					generated.push(
						<circle
							key={`secondary-${orbitIndex}-${i}`}
							cx={centerX + orbitRadius + secondaryRadius}
							cy={centerY}
							r={secondarySize}
							fill={secondaryColor}
							opacity='0.7'
						>
							<animateTransform
								attributeName='transform'
								type='rotate'
								from={`${offset} ${centerX} ${centerY}`}
								to={`${offset + (360 * direction)} ${centerX} ${centerY}`}
								dur={`${duration}s`}
								repeatCount='indefinite'
								calcMode='linear'
							/>
							<animateTransform
								attributeName='transform'
								type='rotate'
								from={`0 ${centerX + orbitRadius} ${centerY}`}
								to={`${360 * -direction} ${centerX + orbitRadius} ${centerY}`}
								dur={`${secondaryDuration}s`}
								repeatCount='indefinite'
								additive='sum'
							/>
						</circle>
					);
				}
			}
		}

		// Complex orbits
		for (let i = 0; i < complexOrbits; i++) {
			const circleSize = randRange(2, 4);
			const colorIndex = i % 4;
			const delay = randRange(0, 5);
			const duration = randRange(15, 25);

			// Create elliptical path
			const rx = randRange(20, 40);
			const ry = randRange(10, 30);
			const cx = 50;
			const cy = randRange(25, 75);

			generated.push(
				<ellipse
					key={`orbit-path-${i}`}
					cx={cx}
					cy={cy}
					rx={rx}
					ry={ry}
					fill='none'
					stroke={colors[colorIndex]}
					strokeWidth='0.3'
					opacity='0.3'
				/>
			);

			generated.push(
				<circle
					key={`complex-orbit-${i}`}
					cx={cx}
					cy={cy - ry}
					r={circleSize}
					fill={colors[colorIndex]}
					opacity='0.8'
				>
					<animateMotion
						path={`M 0 ${-ry} A ${rx} ${ry} 0 1 1 0.1 ${-ry}`}
						dur={`${duration}s`}
						begin={`${delay}s`}
						repeatCount='indefinite'
						calcMode='linear'
					/>
				</circle>
			);
		}
		return generated;
	}, [])
);

export const CircularOrbits: React.FC = () => {
	const elements = useCircularOrbits();
	return (
		<div>
			<svg
				width='100%'
				height='100%'
				viewBox='0 0 100 100'
				preserveAspectRatio='none'
			>
				{elements}
			</svg>
		</div>
	);
};
